import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Staff } from './SchedulerTypes';
import { Tables } from '@/integrations/supabase/types';
import { format } from 'date-fns';

type StaffTodo = Tables<'staff_todos'> & { rating?: number };

interface RatingPerformanceReportProps {
  todos: StaffTodo[];
  staff: Staff[];
}

interface StaffPerfRow {
  staffId: string;
  staffName: string;
  tasksCompleted: number;
  ratedTasks: number;
  avgRating: number; // 1..5 over rated tasks
  onTimeRate: number; // 0..100
  avgDaysLate: number; // over late tasks
  compositeScore: number; // 0..100
  grade: 'Excellent' | 'Good' | 'Fair' | 'Needs Improvement';
}

const PRIORITY_WEIGHT: Record<string, number> = {
  urgent: 1.5,
  high: 1.2,
  medium: 1.0,
  low: 0.8,
};

function computeTaskAdjustedScore(todo: StaffTodo): { score: number; daysLate: number } {
  // Quality: rating to 0..100
  const rating = todo.rating as number | undefined;
  const quality = rating ? Math.max(1, Math.min(5, rating)) / 5 * 100 : NaN; // NaN means unrated

  // Timeliness penalty: 3 points per day late, capped at 30
  let daysLate = 0;
  if (todo.deadline && todo.completed_at) {
    const d = new Date(todo.deadline);
    const c = new Date(todo.completed_at);
    const diffDays = Math.floor((c.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    daysLate = Math.max(0, diffDays);
  }
  const timelinessPenalty = Math.min(30, daysLate * 3);

  // If unrated, return NaN score to exclude from quality-driven composite
  if (Number.isNaN(quality)) {
    return { score: NaN, daysLate };
  }

  const score = Math.max(0, Math.min(100, quality - timelinessPenalty));
  return { score, daysLate };
}

function gradeFromScore(score: number): StaffPerfRow['grade'] {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 55) return 'Fair';
  return 'Needs Improvement';
}

const RatingPerformanceReport: React.FC<RatingPerformanceReportProps> = ({ todos, staff }) => {
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);

  // Completed tasks only
  const completed = useMemo(() => todos.filter(t => t.status === 'completed'), [todos]);

  const perStaff = useMemo<StaffPerfRow[]>(() => {
    const byStaff: Record<string, StaffPerfRow & { _weightedSum: number; _weight: number; _lateDaysSum: number; _lateCount: number; _onTimeWithDeadline: number; _withDeadline: number }>
      = {};

    const nameOf = (id: string) => staff.find(s => s.id === id)?.name || 'Unknown';

    for (const t of completed) {
      const id = t.staff_id;
      if (!byStaff[id]) {
        byStaff[id] = {
          staffId: id,
          staffName: nameOf(id),
          tasksCompleted: 0,
          ratedTasks: 0,
          avgRating: 0,
          onTimeRate: 0,
          avgDaysLate: 0,
          compositeScore: 0,
          grade: 'Fair',
          _weightedSum: 0,
          _weight: 0,
          _lateDaysSum: 0,
          _lateCount: 0,
          _onTimeWithDeadline: 0,
          _withDeadline: 0,
        };
      }

      const row = byStaff[id];
      row.tasksCompleted += 1;

      // Timeliness aggregates
      if (t.deadline && t.completed_at) {
        row._withDeadline += 1;
        const d = new Date(t.deadline);
        const c = new Date(t.completed_at);
        if (c <= d) {
          row._onTimeWithDeadline += 1;
        } else {
          const daysLate = Math.floor((c.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
          row._lateDaysSum += daysLate;
          row._lateCount += 1;
        }
      }

      // Quality & composite aggregates (only rated tasks contribute to composite)
      const { score, daysLate } = computeTaskAdjustedScore(t);
      const rating = t.rating as number | undefined;
      if (rating) {
        row.ratedTasks += 1;
        row.avgRating += rating;
        const weight = PRIORITY_WEIGHT[t.priority] ?? 1.0;
        row._weightedSum += (Number.isNaN(score) ? 0 : score) * weight;
        row._weight += weight;
      }
    }

    return Object.values(byStaff).map(row => {
      // Averages
      if (row.ratedTasks > 0) {
        row.avgRating = Math.round((row.avgRating / row.ratedTasks) * 10) / 10;
      } else {
        row.avgRating = 0;
      }
      row.onTimeRate = row._withDeadline > 0 ? Math.round((row._onTimeWithDeadline / row._withDeadline) * 100) : 0;
      row.avgDaysLate = row._lateCount > 0 ? Math.round((row._lateDaysSum / row._lateCount) * 10) / 10 : 0;

      // Composite score: weighted average of per-task adjusted scores
      const qualityTimeliness = row._weight > 0 ? row._weightedSum / row._weight : 0;

      // Best-practice blend: 70% quality (rating-adjusted), 30% timeliness (on-time rate)
      const composite = 0.7 * qualityTimeliness + 0.3 * row.onTimeRate;
      row.compositeScore = Math.round(composite);
      row.grade = gradeFromScore(row.compositeScore);

      return row;
    }).sort((a, b) => b.compositeScore - a.compositeScore);
  }, [completed, staff]);

  const selectedDetails = useMemo(() => {
    if (!selectedStaff) return [] as (StaffTodo & { score: number; daysLate: number })[];
    const rows = completed.filter(t => t.staff_id === selectedStaff).map(t => {
      const { score, daysLate } = computeTaskAdjustedScore(t);
      return Object.assign({}, t, { score, daysLate });
    });
    // Newest first
    rows.sort((a, b) => {
      const aDate = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const bDate = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return bDate - aDate;
    });
    return rows;
  }, [completed, selectedStaff]);

  const getStaffName = (id: string) => staff.find(s => s.id === id)?.name || 'Unknown';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Rating-Based Performance (Composite)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-center">Completed</TableHead>
                  <TableHead className="text-center">Rated</TableHead>
                  <TableHead className="text-center">Avg Rating</TableHead>
                  <TableHead className="text-center">On-Time</TableHead>
                  <TableHead className="text-center">Avg Days Late</TableHead>
                  <TableHead className="text-center">Score</TableHead>
                  <TableHead className="text-center">Grade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perStaff.map(row => (
                  <TableRow key={row.staffId} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedStaff(row.staffId)}>
                    <TableCell className="font-medium">{row.staffName}</TableCell>
                    <TableCell className="text-center">{row.tasksCompleted}</TableCell>
                    <TableCell className="text-center">{row.ratedTasks}</TableCell>
                    <TableCell className="text-center">{row.avgRating > 0 ? row.avgRating.toFixed(1) : '-'}</TableCell>
                    <TableCell className="text-center">{row.onTimeRate}%</TableCell>
                    <TableCell className="text-center">{row.avgDaysLate > 0 ? row.avgDaysLate : '-'}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Progress value={row.compositeScore} className="w-16" />
                        <span className="font-bold">{row.compositeScore}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={row.grade === 'Excellent' ? 'default' : row.grade === 'Good' ? 'secondary' : 'outline'}>
                        {row.grade}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {perStaff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-6">No data for selected period.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              {selectedStaff ? `Details – ${getStaffName(selectedStaff)}` : 'Click a staff to view details'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDetails.length === 0 ? (
              <div className="text-muted-foreground text-sm">No tasks selected.</div>
            ) : (
              <div className="space-y-2">
                {selectedDetails.map(t => (
                  <div key={t.id} className="p-2 border rounded">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2">
                      <span>Completed: {t.completed_at ? format(new Date(t.completed_at), 'MMM d, yyyy') : '-'}</span>
                      <span>Deadline: {t.deadline ? format(new Date(t.deadline), 'MMM d, yyyy') : '-'}</span>
                      <span>Days late: {t.daysLate}</span>
                      <span>Rating: {t.rating ?? '-'}</span>
                      <span>Score: {Number.isNaN(t.score) ? '-' : Math.round(t.score)}</span>
                      <Badge variant="outline">{t.priority}</Badge>
                    </div>

                    {/* Late Reason Section */}
                    {t.daysLate > 0 && t.late_reason && (
                      <div className="mt-2 text-xs">
                        <div className="font-medium text-muted-foreground">Late Reason:</div>
                        <div className="text-muted-foreground italic mt-1">{t.late_reason}</div>
                      </div>
                    )}

                    {/* Reviewer Comment Section */}
                    {(t as any).reviewer_comment && (
                      <div className="mt-2 text-xs">
                        <div className="font-medium text-blue-700">Reviewer Feedback:</div>
                        <div className="text-blue-900 mt-1 bg-blue-50 p-2 rounded border border-blue-100">
                          {(t as any).reviewer_comment}
                        </div>
                        {(t as any).reviewed_at && (
                          <div className="text-muted-foreground mt-1">
                            Reviewed: {format(new Date((t as any).reviewed_at), 'MMM d, yyyy')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RatingPerformanceReport;

